import { grantRepresentativeAccess, revokeRepresentativeAccess } from '@/api/backend/profile';
import { Container } from '@/components/container';
import { SubNavContainer } from '@/components/sub-nav-container';
import { formatIsoDate } from '@/lib/utils';
import { userProfileRepresentativeQueryOptions } from '@/queryOptions';
import { queryClient } from '@/services/queryClient';
import { useBreadcrumbsStore } from '@/store/breadcrumbs';
import { Badge, Button, ListTable, ListTableCell, ListTableHeader, ListTableRow, useToast } from '@opaz/ui-components';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyRepresentativeForm } from '../../-components/CompanyRepresentativeForm';
import { requireAnyPermission } from '../../../../../../lib/route-guards';
import { PROFILE_PERMISSION } from '../../../../../../lib/constants';

export const Route = createFileRoute('/_auth/profile/company/representative/$representativeId/')({
  component: RouteComponent,
  context: ({ params }) => {
    return {
      getBreadcrumbData: () => ({
        id: params.representativeId,
        translationKey: null,
      }),
    };
  },
  loader: async ({ context, params }) => {
    await requireAnyPermission(context, [PROFILE_PERMISSION.PMS_REPRESENTATIVES]);
    return context.queryClient.ensureQueryData(userProfileRepresentativeQueryOptions(params.representativeId));
  },
  head: () => {
    return {
      meta: [
        {
          title: 'Representative Details - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const { representativeId } = useParams({
    from: '/_auth/profile/company/representative/$representativeId/',
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const { toast } = useToast();

  const { data: profile, isFetching } = useSuspenseQuery(userProfileRepresentativeQueryOptions(representativeId));

  const { updateIdToTextMap } = useBreadcrumbsStore((state) => state);

  const { mutateAsync: revokeAccess, isPending: isRevokeAccessLoading } = useMutation({
    mutationFn: () => revokeRepresentativeAccess(representativeId),
    onSuccess: () => {
      toast({
        message: t('profile.company.representativeDetails.actions.revokeAccessSuccess'),
        variant: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: userProfileRepresentativeQueryOptions(representativeId).queryKey,
      });
    },
  });

  const { mutateAsync: grantAccess, isPending: isGrantAccessLoading } = useMutation({
    mutationFn: () => grantRepresentativeAccess(representativeId),
    onSuccess: () => {
      toast({
        message: t('profile.company.representativeDetails.actions.grantAccessSuccess'),
        variant: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: userProfileRepresentativeQueryOptions(representativeId).queryKey,
      });
    },
  });

  useEffect(() => {
    updateIdToTextMap(representativeId, profile.fullName);
  }, [profile, representativeId, updateIdToTextMap]);

  return (
    <>
      <SubNavContainer>
        <div className="flex gap-4 items-center">
          <div className="flex flex-col">
            <p className="text-[32px] font-bold text-foreground">{profile.fullName}</p>
            <p className="text-xl text-muted-foreground">{profile.company}</p>
          </div>
        </div>
      </SubNavContainer>

      <Container>
        {isEditMode ? (
          <CompanyRepresentativeForm representative={profile} onClose={() => setIsEditMode(false)} />
        ) : (
          <>
            <h1 className="text-foreground text-3xl font-bold mb-4">
              {t('profile.company.representativeDetails.title')}
            </h1>

            <ListTable>
              <ListTableRow>
                <ListTableHeader>{t('profile.company.representativeDetails.listTable.fullName')}</ListTableHeader>
                <ListTableCell>{profile.fullName}</ListTableCell>
              </ListTableRow>

              <ListTableRow>
                <ListTableHeader>{t('profile.company.representativeDetails.listTable.civilId')}</ListTableHeader>
                <ListTableCell>{profile.civilNumber}</ListTableCell>
              </ListTableRow>

              <ListTableRow>
                <ListTableHeader>{t('profile.company.representativeDetails.listTable.email')}</ListTableHeader>
                <ListTableCell>{profile.email}</ListTableCell>
              </ListTableRow>

              <ListTableRow>
                <ListTableHeader>{t('profile.company.representativeDetails.listTable.company')}</ListTableHeader>
                <ListTableCell>{profile.company}</ListTableCell>
              </ListTableRow>

              <ListTableRow>
                <ListTableHeader>Last login</ListTableHeader>
                <ListTableCell>{formatIsoDate(profile.lastLogin, i18n.language)}</ListTableCell>
              </ListTableRow>

              <ListTableRow>
                <ListTableHeader>
                  {t('profile.company.representativeDetails.listTable.accountPermission')}
                </ListTableHeader>
                <ListTableCell>
                  {profile.permissions.map((permission) => t(`profile.permissions.${permission}`)).join(', ')}
                </ListTableCell>
              </ListTableRow>

              <ListTableRow>
                <ListTableHeader>{t('profile.company.representativeDetails.listTable.accountStatus')}</ListTableHeader>
                <ListTableCell>
                  {profile.accountStatus === 'ACTIVE' ? (
                    <Badge type="positive" filled>
                      {t('profile.company.representativeDetails.listTable.accountStatuses.ACTIVE')}
                    </Badge>
                  ) : (
                    <Badge type="negative" filled>
                      {t('profile.company.representativeDetails.listTable.accountStatuses.REVOKED')}
                    </Badge>
                  )}
                </ListTableCell>
              </ListTableRow>
            </ListTable>

            <div className="flex gap-4 mt-12">
              {profile.accountStatus === 'ACTIVE' ? (
                <Button
                  disabled={isFetching || isRevokeAccessLoading}
                  variant="destructive"
                  onClick={() => revokeAccess()}
                >
                  {t('profile.company.representativeDetails.actions.revokeAccess')}
                </Button>
              ) : (
                <Button disabled={isFetching || isGrantAccessLoading} onClick={() => grantAccess()}>
                  {t('profile.company.representativeDetails.actions.grantAccess')}
                </Button>
              )}

              {profile.accountStatus === 'ACTIVE' && (
                <Button
                  variant="secondary"
                  disabled={isFetching || isGrantAccessLoading || isRevokeAccessLoading}
                  onClick={() => setIsEditMode(true)}
                >
                  {t('profile.company.representativeDetails.actions.edit')}
                </Button>
              )}
            </div>
          </>
        )}
      </Container>
    </>
  );
}
